import Grid from '@ilg-totallymoney/ui/components/Grid'
import Stack from '@ilg-totallymoney/ui/components/Stack'
import { captureException } from '@sentry/nextjs'

import ErrorBoundary from '@components/ErrorBoundary'

import useAppQueryState from '@hooks/useAppQueryState'

import { useUnscoredCategoriesQuery } from '@queries/useUnscoredCategoriesQuery'

import DynamicUnscoredFrequentlyAskedQuestions from '@routes/UnscoredTableRoute/components/UnscoredFrequentlyAskedQuestions/DynamicUnscoredFrequentlyAskedQuestions'

import LoansLegalDisclaimer from './components/LoansLegalDisclaimer'
import ProviderLogos from './components/ProviderLogos/ProviderLogos'
import SecuredLoanDisclaimer from './components/SecuredLoanDisclaimer'
import UnscoredPageHeader from './components/UnscoredPageHeader/UnscoredPageHeader'
import UnscoredTable from './components/UnscoredTable'
import UnscoredTableError from './components/UnscoredTableError'
import UnscoredTableSkeleton from './components/UnscoredTableSkeleton'

interface UnscoredTableRouteProps {
    initialQueryState: {
        category: string
        loanAmount?: number
        loanTerm?: number
    }
}

const UnscoredTableRoute = ({ initialQueryState }: UnscoredTableRouteProps) => {
    const { appQueryState } = useAppQueryState()

    const unscoredCategoriesResponse = useUnscoredCategoriesQuery({
        query: {
            loanAmount: Number(
                appQueryState.loanAmount ?? initialQueryState.loanAmount
            ),
            loanTerm: Number(
                appQueryState.loanTerm ?? initialQueryState.loanTerm
            ),
            mockId: appQueryState.mockId,
        },
    })

    const categoryPath = appQueryState.category! ?? initialQueryState.category

    return (
        <>
            <UnscoredPageHeader activeCategoryId={appQueryState.categoryId!} />
            <ErrorBoundary
                fallback={<UnscoredTableError />}
                onError={({ error }) => captureException(error)}
            >
                <>
                    {unscoredCategoriesResponse.isLoading ? (
                        <UnscoredTableSkeleton />
                    ) : null}

                    {unscoredCategoriesResponse.isSuccess ? (
                        <UnscoredTable
                            unscoredCategories={
                                unscoredCategoriesResponse.data
                                    .unscoredCategories
                            }
                            alternativeProducts={
                                unscoredCategoriesResponse.data
                                    .alternativeProducts
                            }
                            filter={unscoredCategoriesResponse.data.filter}
                            isUpdatingResults={
                                unscoredCategoriesResponse.isFetching
                            }
                            activeCategoryId={appQueryState.categoryId!}
                        />
                    ) : null}

                    {unscoredCategoriesResponse.isError ? (
                        <UnscoredTableError />
                    ) : null}

                    <Grid>
                        <Stack space="space30" gridColumn="1/13" py="space50">
                            <ProviderLogos
                                category={appQueryState.categoryId!}
                            />

                            <DynamicUnscoredFrequentlyAskedQuestions
                                category={categoryPath}
                            />

                            {categoryPath !== 'homeowner' ? (
                                <LoansLegalDisclaimer />
                            ) : null}
                        </Stack>
                    </Grid>
                </>
            </ErrorBoundary>
        </>
    )
}

export default UnscoredTableRoute
